import { useCallback, useEffect, useRef, useState } from "react";
import type { WSMessage, WSStatus } from "../types";

const WORKER_URL = import.meta.env.VITE_WORKER_URL ?? "";
const RECONNECT_DELAY_MS = 800;
const MAX_RECONNECTS = 10;

export function useWebSocket(sessionId: string | null) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectCount = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [status, setStatus] = useState<WSStatus>("disconnected");
  const messageHandlers = useRef<Array<(msg: WSMessage) => void>>([]);

  const send = useCallback((msg: WSMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  const onMessage = useCallback((handler: (msg: WSMessage) => void) => {
    messageHandlers.current.push(handler);
    return () => {
      messageHandlers.current = messageHandlers.current.filter((h) => h !== handler);
    };
  }, []);

  const connect = useCallback((sid: string) => {
    if (wsRef.current) {
      wsRef.current.close();
    }

    setStatus("connecting");

    const wsUrl = WORKER_URL
      ? `${WORKER_URL.replace(/^http/, "ws")}/ws/${sid}`
      : `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}/ws/${sid}`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus("connected");
      reconnectCount.current = 0;
    };

    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data as string) as WSMessage;
        messageHandlers.current.forEach((h) => h(msg));
      } catch {
        // ignore malformed
      }
    };

    ws.onerror = () => {
      setStatus("error");
    };

    ws.onclose = () => {
      setStatus("disconnected");
      wsRef.current = null;
      if (reconnectCount.current < MAX_RECONNECTS) {
        reconnectCount.current++;
        reconnectTimer.current = setTimeout(() => connect(sid), RECONNECT_DELAY_MS);
      }
    };
  }, []);

  useEffect(() => {
    if (sessionId) {
      connect(sessionId);
    }
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [sessionId, connect]);

  return { status, send, onMessage };
}
